import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Section, Eyebrow, Lede } from "../components/site/Section";
import { getMyCast, getMyArchive, useAuth, setUsername as setUsernameApi } from "../lib/auth";
import { NATIONS_LIST, NATION_BY_SLUG, RARITY_STYLES } from "../lib/archive";
import { toast } from "sonner";

const RARITY_BORDER = { C: "#8C5A2B", B: "#B8B8B8", A: "#3F7BD0", S: "#C5A059" };

function UsernameModal({ onDone }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const onSave = async (e) => {
    e?.preventDefault?.();
    if (v.trim().length < 3) {
      toast.error("Username must be at least 3 characters.");
      return;
    }
    setBusy(true);
    try {
      await setUsernameApi(v.trim());
      toast.success("Sigil sealed. Welcome to the Archive.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set username.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-5" data-testid="username-modal">
      <form onSubmit={onSave} className="bg-[#0A0A0A] border border-[#C5A059]/40 max-w-md w-full p-7" style={{ boxShadow: "0 0 80px -30px #C5A059" }}>
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">Choose Your Sigil</div>
        <h2 className="font-display text-3xl text-[#EAE0D5] mt-3">Name yourself.</h2>
        <p className="text-sm text-[#A39E99] mt-3 leading-relaxed">
          This name appears on the leaderboard and lives beside every record you recover.
          3 – 24 characters. Letters, numbers, underscores.
        </p>
        <input
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          maxLength={24}
          placeholder="e.g. ravenwriter"
          data-testid="username-input"
          className="mt-5 w-full bg-[#050505] border border-white/10 px-4 py-3 text-[#EAE0D5] font-display text-xl tracking-wide focus:border-[#C5A059] outline-none"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="username-save-btn"
          className="mt-5 w-full bg-[#C5A059] text-black px-6 py-3 text-xs tracking-[0.26em] uppercase hover:bg-[#D4AF68] disabled:opacity-50"
        >
          {busy ? "Sealing…" : "Seal My Sigil"}
        </button>
      </form>
    </div>
  );
}

export default function MyCastPage() {
  const { user, loading, refresh } = useAuth();
  const [cast, setCast] = useState([]);
  const [stats, setStats] = useState(null);
  const [needsUsername, setNeedsUsername] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    setNeedsUsername(!user.username);
    (async () => {
      try {
        const [c, s] = await Promise.all([getMyCast(), getMyArchive()]);
        setCast(c.characters || []);
        setStats(s);
      } catch { /* ignore */ }
    })();
  }, [user, loading]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div data-testid="my-cast-page">
      {needsUsername && (
        <UsernameModal onDone={async () => { await refresh(); setNeedsUsername(false); }} />
      )}

      <Section testId="mycast-hero" className="pt-12 md:pt-24">
        <Eyebrow>Your Personal Cast</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4 max-w-4xl">
          Records <span className="text-[#C5A059] italic font-light">you</span> have recovered.
        </h1>
        <Lede>
          Every figure here was unsealed by your own hand. Names, nations, full lore — yours.
        </Lede>

        {stats && (
          <div className="mt-10 grid md:grid-cols-12 gap-4" data-testid="mycast-stats">
            <div className="md:col-span-4 bg-[#0A0A0A] border border-white/5 p-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">Overall</div>
              <div className="flex items-baseline gap-2 mt-2">
                <div className="font-display text-5xl text-[#C5A059] tabular-nums">{stats.unlocked}</div>
                <div className="font-display text-xl text-[#737373]">/ {stats.total}</div>
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#737373] mt-2">
                {Math.round((stats.unlocked / Math.max(1, stats.total)) * 100)}% complete
              </div>
            </div>
            <div className="md:col-span-4 bg-[#0A0A0A] border border-white/5 p-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">Accuracy</div>
              <div className="flex items-baseline gap-2 mt-2">
                <div className="font-display text-5xl text-[#EAE0D5] tabular-nums">{stats.accuracy}<span className="text-xl text-[#737373]">%</span></div>
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#737373] mt-2">
                {stats.correct} correct of {stats.attempts}
              </div>
            </div>
            <div className="md:col-span-4 bg-[#0A0A0A] border border-white/5 p-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">Last Discovery</div>
              <p className="font-display text-lg text-[#EAE0D5] mt-2 truncate">
                {stats.last_discovery ? new Date(stats.last_discovery).toLocaleString() : "—"}
              </p>
              <div className="mt-3">
                <Link to="/leaderboard" className="text-[10px] tracking-[0.24em] uppercase text-[#C5A059] hover:underline" data-testid="mycast-leaderboard-link">
                  See your standing →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Nation completion bars */}
        {stats && (
          <div className="mt-10" data-testid="mycast-nations-breakdown">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059] mb-5">By Nation</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {NATIONS_LIST.map((n) => {
                const s = stats.nations[n.slug] || { total: 0, unlocked: 0 };
                const pct = s.total ? Math.round((s.unlocked / s.total) * 100) : 0;
                return (
                  <div key={n.slug} className="bg-[#0A0A0A] border border-white/5 p-4">
                    <div className="flex items-baseline justify-between">
                      <div className="text-xs tracking-[0.24em] uppercase" style={{ color: n.accent }}>{n.name}</div>
                      <div className="text-xs text-[#A39E99] tabular-nums">{s.unlocked} / {s.total}</div>
                    </div>
                    <div className="h-[3px] bg-white/5 mt-3 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full"
                        style={{ background: n.accent }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Section>

      <Section testId="mycast-list" className="pt-0 pb-24">
        <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] mb-7">
          <span>Your Cast — {cast.length}</span>
        </div>
        {cast.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/5 p-10 md:p-16 text-center" data-testid="mycast-empty">
            <Sparkles size={20} className="mx-auto text-[#C5A059]" />
            <p className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-5">
              The Archive is waiting for you.
            </p>
            <p className="text-sm text-[#A39E99] mt-3 max-w-md mx-auto">
              No records under your sigil yet. Crack the current signal to begin.
            </p>
            <Link
              to="/archive/drop"
              data-testid="mycast-empty-cta"
              className="mt-6 inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-[11px] tracking-[0.26em] uppercase hover:bg-[#D4AF68]"
            >
              Open the Signal <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="mycast-grid">
            {cast.map((c) => {
              const nation = NATION_BY_SLUG[c.nation];
              const border = RARITY_BORDER[c.rarity] || RARITY_BORDER.C;
              const rstyle = RARITY_STYLES[c.rarity] || RARITY_STYLES.C;
              return (
                <li
                  key={c.id}
                  className="bg-[#0A0A0A] border-2 overflow-hidden"
                  style={{ borderColor: `${border}55`, boxShadow: `0 0 40px -22px ${border}` }}
                >
                  <div className="aspect-[3/4] bg-[#050505] overflow-hidden">
                    {c.image && <img src={c.image} alt={c.name} className="w-full h-full object-cover" style={{ objectPosition: c.image_position || "center" }} loading="lazy" />}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: rstyle.color }}>
                        {rstyle.label}
                      </div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[#A39E99]">
                        {nation?.name || c.nation}
                      </div>
                    </div>
                    <h3 className="font-display text-xl text-[#EAE0D5] mt-2 leading-tight">{c.name}</h3>
                    {c.title && <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373] mt-1">{c.title}</div>}
                    {c.quote && <p className="italic text-sm text-[#A39E99] mt-3 leading-relaxed">"{c.quote}"</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}
